import React, { useState, useEffect } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip
} from '@mui/material';
import { UserRole, roleLabels } from '../types/User';
import './UserManagement.css';

interface UserData {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  password?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: UserRole.GENERAL_USER
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    const savedUsers = JSON.parse(localStorage.getItem('users') || '[]');
    setUsers(savedUsers);
  };

  const handleOpenDialog = (user?: UserData) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        password: '',
        role: user.role
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        password: '',
        role: UserRole.GENERAL_USER
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleSaveUser = () => {
    const savedUsers = JSON.parse(localStorage.getItem('users') || '[]');
    
    if (editingUser) {
      // Update existing user
      const updatedUsers = savedUsers.map((u: UserData) => {
        if (u.id === editingUser.id) {
          return {
            ...u,
            email: formData.email,
            name: formData.name,
            role: formData.role,
            ...(formData.password && { password: formData.password })
          };
        }
        return u;
      });
      localStorage.setItem('users', JSON.stringify(updatedUsers));
    } else {
      // Add new user
      const newUser = {
        id: Date.now().toString(),
        email: formData.email,
        name: formData.name,
        password: formData.password,
        role: formData.role
      };
      savedUsers.push(newUser);
      localStorage.setItem('users', JSON.stringify(savedUsers));
    }
    
    loadUsers();
    handleCloseDialog();
  };

  const handleDeleteUser = (userId: string) => {
    if (window.confirm('정말로 이 사용자를 삭제하시겠습니까?')) {
      const savedUsers = JSON.parse(localStorage.getItem('users') || '[]');
      const filteredUsers = savedUsers.filter((u: UserData) => u.id !== userId);
      localStorage.setItem('users', JSON.stringify(filteredUsers));
      loadUsers();
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch(role) {
      case UserRole.SYSTEM_ADMIN:
        return 'error';
      case UserRole.GENERAL_ADMIN:
        return 'warning';
      case UserRole.GENERAL_USER:
        return 'info';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 100,
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'name', 
      headerName: '이름', 
      width: 150,
      headerAlign: 'left',
      align: 'left'
    },
    { 
      field: 'email', 
      headerName: '이메일', 
      width: 250,
      headerAlign: 'left',
      align: 'left'
    },
    { 
      field: 'role', 
      headerName: '권한', 
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={roleLabels[params.value as UserRole]}
          color={getRoleColor(params.value as UserRole)}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Button 
            size="small" 
            variant="outlined" 
            color="primary"
            onClick={() => handleOpenDialog(params.row as UserData)}
          >
            수정
          </Button>
          <Button 
            size="small" 
            variant="outlined" 
            color="error"
            onClick={() => handleDeleteUser(params.row.id)}
            disabled={params.row.role === UserRole.SYSTEM_ADMIN}
          >
            삭제
          </Button>
        </Box>
      )
    }
  ];

  return (
    <div className="user-management-container">
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <div>
            <Typography variant="h4" gutterBottom>
              사용자 관리
            </Typography>
            <Typography variant="body1" color="textSecondary">
              시스템 사용자를 관리하고 권한을 설정합니다.
            </Typography>
          </div>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => handleOpenDialog()}
          >
            새 사용자 추가
          </Button>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={users}
            columns={columns}
            pageSizeOptions={[5, 10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? '사용자 수정' : '새 사용자 추가'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="이름"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="이메일"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={editingUser ? '비밀번호 (변경시에만 입력)' : '비밀번호'}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              required={!editingUser}
            />
            <TextField
              select
              label="권한"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              fullWidth
            >
              {Object.entries(roleLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained" 
            color="primary"
            disabled={!formData.name || !formData.email || (!editingUser && !formData.password)}
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserManagement;